import { useEffect } from "react";
import NextLink from "next/link";
import { useRouter } from "next/router";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Heading,
  SkeletonText,
  VStack,
} from "@chakra-ui/react";
import { RiSearchLine } from "react-icons/ri";
import useSWR from "swr";
import CopyLinkButton from "components/common/copy-link-button";
import TextLink from "components/common/link/text-link";
import {
  CardTable,
  CardTbody,
  CardTd,
  CardTh,
  CardTr,
  SkeletonRows,
} from "components/profile/card-table";
import CreateCitationButton from "components/profile/citation/create-citation-button";
import EditProfileButton from "components/profile/edit-profile-button";
import LimitWarning from "components/profile/limit-warning";
import ProfileBody from "components/profile/profile-body";
import ProfileCard, {
  ProfileCardHeader,
} from "components/profile/profile-card";
import {
  ProfileHeader,
  ProfileHeaderName,
} from "components/profile/profile-header";
import RequestUpdateButton from "components/profile/update-modals/request-update-button";
import SuggestUpdateButton from "components/profile/update-modals/suggest-update-button";
import usePermission from "hooks/use-permission";
import useUser from "hooks/use-user";
import { isSelectUser as isSelectUserFn } from "utils/access-group";
import fetcher from "utils/fetcher";
import getSiteUrl from "utils/get-site-url";
import type { CompanyContactResponse } from "types/api/contact/details";
import ContactExportMenu from "./contact-export";

interface ContactNoCompanyUnpublishedProps {
  data: string;
}

const ContactNoCompanyUnpublished = ({
  data,
}: ContactNoCompanyUnpublishedProps) => {
  const { data: individualContact, error } = useSWR<CompanyContactResponse>(
    `/system/company-contact/${data}`,
    fetcher
  );

  const router = useRouter();

  const hasCompanyContactViewPermission = usePermission("companyContactView");

  const user = useUser();
  const isFreeUser = user?.access?.group === "free";
  const isPublicUser = !user?.isLoggedIn || false;
  const isSelectUser = isSelectUserFn(user?.access?.group);
  const isCorporateUser = user?.access?.group === "corporate";
  const isAcademicUser = user?.access?.group === "academic";
  const isAcademicDirectUser = user?.access?.group === "academic-direct";

  const contactFullName = individualContact ? (
    `${individualContact.firstName} ${individualContact.lastName}`
  ) : (
    <SkeletonText mt="5" noOfLines={1} skeletonHeight="7" width="60" />
  );
  const contactId = individualContact ? `${individualContact.id}` : null;
  const numContactId = [{ id: parseInt(contactId, 10), type: "company" }];

  useEffect(() => {
    if (
      !hasCompanyContactViewPermission &&
      (isAcademicUser || isAcademicDirectUser)
    ) {
      router.push(`/company/${router.query.slug}`);
    }
  }, [
    hasCompanyContactViewPermission,
    isAcademicUser,
    isAcademicDirectUser,
    router,
  ]);

  if (!hasCompanyContactViewPermission && (isFreeUser || isPublicUser)) {
    return (
      <Alert
        status="warning"
        alignItems="center"
        py={3}
        borderTopWidth="1px"
        borderBottomWidth="1px"
      >
        <AlertIcon />
        <AlertDescription fontSize={["sm", "sm", "md"]} pr={8} lineHeight={1.5}>
          <TextLink href="/upgrade">Upgrade now</TextLink> to unlock Contact
          Information
        </AlertDescription>
      </Alert>
    );
  }

  if (!hasCompanyContactViewPermission && (isSelectUser || isCorporateUser)) {
    return (
      <Alert
        status="warning"
        alignItems="center"
        py={3}
        borderTopWidth="1px"
        borderBottomWidth="1px"
      >
        <AlertIcon />
        <AlertDescription fontSize={["sm", "sm", "md"]} pr={8} lineHeight={1.5}>
          <TextLink href="https://www.privco.com/contact">
            Contact Sales
          </TextLink>{" "}
          to unlock and view Contact Information
        </AlertDescription>
      </Alert>
    );
  }

  if (error?.info?.message === "User has reached contact view limit") {
    return <LimitWarning />;
  }

  return (
    <>
      <Box bg="white" top={0} boxShadow="sm">
        <ProfileHeader style={{ marginLeft: "-3%" }}>
          {individualContact && individualContact.companyName ? (
            <ProfileHeaderName name={individualContact.companyName}>
              {isPublicUser ? (
                <SuggestUpdateButton
                  identifier={{
                    name: individualContact.companyName,
                    id: 0,
                    slug: null,
                  }}
                  type="company"
                />
              ) : (
                <RequestUpdateButton isInternational={false} />
              )}

              <CreateCitationButton
                name={individualContact.companyName}
                slug=""
                type="company"
              />

              <CopyLinkButton
                label="Copy Profile Link"
                variant="solid"
                data-cy="copy-company-profile-link"
              />

              <EditProfileButton
                profileId={0}
                type="company"
                data-cy="edit-company-button"
              />
            </ProfileHeaderName>
          ) : (
            <ProfileHeader style={{ marginLeft: "5%" }}>
              <SkeletonText noOfLines={1} skeletonHeight="10" width="60" />
            </ProfileHeader>
          )}
        </ProfileHeader>
        <ProfileHeader>
          <VStack alignItems="flex-start" spacing={6}>
            <Heading size="md" as="h1" fontWeight="medium">
              This profile is currently being processed.
            </Heading>

            <Heading size="md" as="h2" fontWeight="medium">
              Please reach out to our{" "}
              <TextLink isExternal href="mailto:research@privco.com">
                research team
              </TextLink>{" "}
              for more information.
            </Heading>

            <Button
              as={NextLink}
              href="/search/company"
              size="sm"
              colorScheme="indigo"
              leftIcon={<RiSearchLine />}
              data-cy="browse-other-companies"
            >
              Browse other companies
            </Button>
          </VStack>
        </ProfileHeader>
      </Box>
      <ProfileBody>
        <ProfileCard>
          <ProfileCardHeader>
            {contactFullName}{" "}
            {individualContact && (
              <ContactExportMenu contacts={numContactId} exportType="contact" />
            )}
          </ProfileCardHeader>
          <CardTable>
            {individualContact ? (
              <CardTbody>
                <CardTr>
                  <CardTh borderBottom="0px" fontWeight="500">
                    Title
                  </CardTh>
                  <CardTh borderBottom="0px" fontWeight="500">
                    City, State, Country
                  </CardTh>
                </CardTr>
                <CardTr>
                  <CardTd
                    borderBottom="0px"
                    fontWeight="800"
                    color="#4b5563"
                    paddingLeft="0.5rem"
                    paddingBottom="0"
                  >
                    {individualContact.jobTitles.length !== 0
                      ? individualContact.jobTitles.map((jobTitle) => (
                          <CardTr key={individualContact.id}>{jobTitle}</CardTr>
                        ))
                      : "--"}
                  </CardTd>
                  <CardTd
                    borderBottom="0px"
                    fontWeight="800"
                    color="#4b5563"
                    display="block"
                  >
                    {individualContact.personalCity
                      ? individualContact.personalCity
                      : "--"}
                    ,{" "}
                    {individualContact.personalState
                      ? individualContact.personalState
                      : "--"}
                    ,{" "}
                    {individualContact.personalCountry
                      ? individualContact.personalCountry
                      : "--"}
                  </CardTd>
                </CardTr>
                <CardTr>
                  <CardTh borderBottom="0px" fontWeight="500">
                    Seniority
                  </CardTh>
                  <CardTh borderBottom="0px" fontWeight="500">
                    Direct Phone
                  </CardTh>
                </CardTr>
                <CardTr>
                  <CardTd
                    borderBottom="0px"
                    fontWeight="800"
                    color="#4b5563"
                    display="block"
                  >
                    {individualContact.seniorityLevel
                      ? individualContact.seniorityLevel
                      : "--"}
                  </CardTd>
                  <CardTd
                    borderBottom="0px"
                    fontWeight="800"
                    color="#4b5563"
                    paddingLeft="0.5rem"
                    paddingBottom="0"
                  >
                    {individualContact.directNumbers.length !== 0
                      ? individualContact.directNumbers.map((directNumber) => (
                          <CardTr key={individualContact.id}>
                            <TextLink
                              isExternal
                              hasIcon
                              href={`tel:${directNumber}`}
                              variant="truncate"
                            >
                              {directNumber}
                            </TextLink>
                          </CardTr>
                        ))
                      : "--"}
                  </CardTd>
                </CardTr>
                <CardTr>
                  <CardTh borderBottom="0px" fontWeight="500">
                    Department
                  </CardTh>
                  <CardTh borderBottom="0px" fontWeight="500">
                    Mobile
                  </CardTh>
                </CardTr>
                <CardTr>
                  <CardTd
                    borderBottom="0px"
                    fontWeight="800"
                    color="#4b5563"
                    display="block"
                  >
                    {individualContact.department
                      ? individualContact.department
                      : "--"}
                  </CardTd>
                  <CardTd
                    borderBottom="0px"
                    fontWeight="800"
                    color="#4b5563"
                    paddingLeft="0.5rem"
                    paddingBottom="0"
                  >
                    {individualContact.mobileNumbers.length !== 0
                      ? individualContact.mobileNumbers.map((mobileNumber) => (
                          <CardTr key={individualContact.id}>
                            <TextLink
                              isExternal
                              hasIcon
                              href={`tel:${mobileNumber}`}
                              variant="truncate"
                            >
                              {mobileNumber}
                            </TextLink>
                          </CardTr>
                        ))
                      : "--"}
                  </CardTd>
                </CardTr>
                <CardTr>
                  <CardTh borderBottom="0px" fontWeight="500">
                    Business Email
                  </CardTh>
                  <CardTh borderBottom="0px" fontWeight="500">
                    LinkedIn
                  </CardTh>
                </CardTr>
                <CardTr>
                  <CardTd borderBottom="0px" fontWeight="800" color="#4b5563">
                    {individualContact.businessEmail
                      ? individualContact.businessEmail
                      : "--"}
                  </CardTd>
                  <CardTd borderBottom="0px" fontWeight="800" color="#4b5563">
                    {individualContact.personalLinkedin ? (
                      <TextLink
                        isExternal
                        hasIcon
                        href={getSiteUrl(individualContact.personalLinkedin)}
                        variant="truncate"
                      >
                        {individualContact.personalLinkedin}
                      </TextLink>
                    ) : (
                      "--"
                    )}
                  </CardTd>
                </CardTr>
                <CardTr>
                  <CardTh borderBottom="0px" fontWeight="500">
                    Year Last Seen
                  </CardTh>
                </CardTr>
                <CardTr>
                  <CardTd
                    borderBottom="0px"
                    fontWeight="800"
                    color="#4b5563"
                    paddingLeft="0.5rem"
                    paddingBottom="0"
                  >
                    {individualContact.yearLastSeen
                      ? individualContact.yearLastSeen
                      : "--"}
                  </CardTd>
                </CardTr>
              </CardTbody>
            ) : (
              <SkeletonRows />
            )}
          </CardTable>
        </ProfileCard>
      </ProfileBody>
    </>
  );
};

export default ContactNoCompanyUnpublished;
